import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarClock, MapPin, UserCheck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { sectionsApi } from '@/services/api/endpoints/academic';
import { teachersApi } from '@/services/api/endpoints/staff';
import { examScheduleApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Examination, ExamSchedule } from '@/types/examination';
import { formatDate, orDash } from '@/utils/format';

const schema = z
  .object({
    examinationSubjectId: z.string().uuid('Select a paper'),
    sectionId: z.string().uuid('Select a section'),
    examDate: z.string().min(1, 'A date is required'),
    startTime: z.string().min(1, 'A start time is required'),
    endTime: z.string().min(1, 'An end time is required'),
    room: z.string().max(50).optional(),
    invigilatorTeacherId: z.string(),
    instructions: z.string().max(500).optional(),
  })
  .refine((values) => values.endTime > values.startTime, {
    path: ['endTime'],
    message: 'The sitting must end after it starts',
  });

type FormValues = z.infer<typeof schema>;

/** `HH:mm:ss` from the server, `HH:mm` in the input. */
function toInputTime(value: string): string {
  return value.slice(0, 5);
}

/**
 * The examination timetable.
 *
 * Grouped by day, because that is how an exam timetable is read and posted on a
 * noticeboard. Clashes are not checked here: the backend has unique indexes on
 * section, room and invigilator, so a double-booking is refused by the database
 * rather than by a check this screen could race against.
 */
export function ExamSchedulePanel({ examination }: { examination: Examination }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ExamSchedule | null>(null);
  const [removing, setRemoving] = useState<ExamSchedule | null>(null);

  const canManage = can(PERMISSIONS.EXAM_SCHEDULE_MANAGE);
  const papers = examination.subjects ?? [];

  const schedule = useQuery({
    queryKey: queryKeys.examinations.schedule(examination.id),
    queryFn: () => examScheduleApi.list(examination.id),
    enabled: can(PERMISSIONS.EXAM_SCHEDULE_VIEW),
  });

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ gradeOfferingId: examination.gradeOfferingId }),
    queryFn: () =>
      sectionsApi.list({
        page: 0,
        size: 100,
        gradeOfferingId: examination.gradeOfferingId,
        status: 'ACTIVE',
      }),
    enabled: open,
  });

  const teachers = useQuery({
    queryKey: queryKeys.teachers.list({ picker: true }),
    queryFn: () => teachersApi.list({ page: 0, size: 200 }),
    enabled: open && can(PERMISSIONS.TEACHER_VIEW),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      examinationSubjectId: '',
      sectionId: '',
      examDate: examination.startDate,
      startTime: '09:00',
      endTime: '11:00',
      room: '',
      invigilatorTeacherId: '',
      instructions: '',
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.examinations.all });
  };

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        examinationSubjectId: values.examinationSubjectId,
        sectionId: values.sectionId,
        examDate: values.examDate,
        startTime: values.startTime,
        endTime: values.endTime,
        ...(values.room !== undefined && values.room !== '' ? { room: values.room } : {}),
        ...(values.invigilatorTeacherId !== ''
          ? { invigilatorTeacherId: values.invigilatorTeacherId }
          : {}),
        ...(values.instructions !== undefined && values.instructions !== ''
          ? { instructions: values.instructions }
          : {}),
      };
      return editing === null
        ? examScheduleApi.create(examination.id, payload)
        : examScheduleApi.update(examination.id, editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Paper scheduled' : 'Sitting updated');
      setOpen(false);
      setEditing(null);
      refresh();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const remove = useMutation({
    mutationFn: (sitting: ExamSchedule) => examScheduleApi.remove(examination.id, sitting.id),
    onSuccess: () => {
      toast.success('Sitting removed');
      setRemoving(null);
      refresh();
    },
    onError: (error) => {
      setRemoving(null);
      handleApiError(error);
    },
  });

  // One group per day, in date order, each group in start-time order.
  const days = useMemo(() => {
    const grouped = new Map<string, ExamSchedule[]>();
    for (const sitting of schedule.data ?? []) {
      const bucket = grouped.get(sitting.examDate);
      if (bucket === undefined) {
        grouped.set(sitting.examDate, [sitting]);
      } else {
        bucket.push(sitting);
      }
    }
    return [...grouped.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, sittings]) => ({
        date,
        sittings: [...sittings].sort((a, b) => a.startTime.localeCompare(b.startTime)),
      }));
  }, [schedule.data]);

  function openCreate() {
    setEditing(null);
    form.reset({
      examinationSubjectId: papers[0]?.id ?? '',
      sectionId: '',
      examDate: examination.startDate,
      startTime: '09:00',
      endTime: '11:00',
      room: '',
      invigilatorTeacherId: '',
      instructions: '',
    });
    setOpen(true);
  }

  function openEdit(sitting: ExamSchedule) {
    setEditing(sitting);
    form.reset({
      examinationSubjectId: sitting.examinationSubjectId,
      sectionId: sitting.sectionId,
      examDate: sitting.examDate,
      startTime: toInputTime(sitting.startTime),
      endTime: toInputTime(sitting.endTime),
      room: sitting.room ?? '',
      invigilatorTeacherId: sitting.invigilatorTeacherId ?? '',
      instructions: sitting.instructions ?? '',
    });
    setOpen(true);
  }

  if (!can(PERMISSIONS.EXAM_SCHEDULE_VIEW)) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Timetable"
          description="Which section sits which paper, when and where."
          icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
          action={
            canManage && papers.length > 0 ? (
              <Button size="sm" onClick={openCreate}>
                Schedule a paper
              </Button>
            ) : undefined
          }
        />

        {schedule.isError ? (
          <ErrorState error={schedule.error} onRetry={() => void schedule.refetch()} />
        ) : schedule.isPending ? (
          <LoadingState label="Loading the timetable" />
        ) : days.length === 0 ? (
          <EmptyState
            title="Nothing scheduled yet"
            description={
              papers.length === 0
                ? 'Add the papers first — a sitting is a paper, a section and an hour.'
                : 'Schedule each paper for each section that will sit it.'
            }
            icon={<CalendarClock className="h-6 w-6" aria-hidden="true" />}
          />
        ) : (
          <div className="divide-y divide-line">
            {days.map((day) => (
              <div key={day.date} className="px-4 py-3.5">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-subtle">
                  {formatDate(day.date)}
                </h3>
                <ul className="mt-2.5 space-y-2">
                  {day.sittings.map((sitting) => (
                    <li
                      key={sitting.id}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border border-line bg-surface-2 px-3.5 py-2.5"
                    >
                      <span className="w-[105px] shrink-0 font-mono text-xs tabular-nums text-ink">
                        {toInputTime(sitting.startTime)}–{toInputTime(sitting.endTime)}
                      </span>
                      <span className="min-w-[140px] flex-1 text-sm font-medium text-ink">
                        {sitting.subjectName}
                      </span>
                      <span className="text-sm text-muted">{sitting.sectionName}</span>
                      {sitting.room !== undefined && sitting.room !== '' && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {sitting.room}
                        </span>
                      )}
                      {sitting.invigilatorName !== undefined && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted">
                          <UserCheck className="h-3.5 w-3.5" aria-hidden="true" />
                          {sitting.invigilatorName}
                        </span>
                      )}
                      <span className="text-xs tabular-nums text-subtle">
                        {sitting.durationMinutes} min
                      </span>
                      {canManage && (
                        <span className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(sitting)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setRemoving(sitting)}>
                            Remove
                          </Button>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'Schedule a paper' : `Move ${editing.subjectName}`}
        description="A room or invigilator already booked at that hour is refused."
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={form.handleSubmit((values) => save.mutate(values))}
              loading={save.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={form.handleSubmit((values) => save.mutate(values))}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Paper"
              required
              error={form.formState.errors.examinationSubjectId?.message}
              {...form.register('examinationSubjectId')}
            >
              <option value="">Select a paper…</option>
              {papers.map((paper) => (
                <option key={paper.id} value={paper.id}>
                  {paper.subjectName}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Section"
              required
              error={form.formState.errors.sectionId?.message}
              {...form.register('sectionId')}
            >
              <option value="">Select a section…</option>
              {(sections.data?.data ?? []).map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              label="Date"
              type="date"
              required
              min={examination.startDate}
              max={examination.endDate}
              hint={`Within ${formatDate(examination.startDate)} – ${formatDate(examination.endDate)}`}
              error={form.formState.errors.examDate?.message}
              {...form.register('examDate')}
            />
            <TextField
              label="Starts"
              type="time"
              required
              error={form.formState.errors.startTime?.message}
              {...form.register('startTime')}
            />
            <TextField
              label="Ends"
              type="time"
              required
              error={form.formState.errors.endTime?.message}
              {...form.register('endTime')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Room"
              placeholder="Hall A"
              hint="Optional. Two sittings cannot share a room at the same hour."
              error={form.formState.errors.room?.message}
              {...form.register('room')}
            />
            <SelectField
              label="Invigilator"
              hint="Optional. One teacher cannot invigilate two rooms at once."
              error={form.formState.errors.invigilatorTeacherId?.message}
              {...form.register('invigilatorTeacherId')}
            >
              <option value="">Not assigned</option>
              {(teachers.data?.data ?? []).map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName ?? teacher.employeeNumber}
                </option>
              ))}
            </SelectField>
          </div>

          <TextField
            label="Instructions"
            placeholder="Calculators permitted, no rough sheets"
            error={form.formState.errors.instructions?.message}
            {...form.register('instructions')}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this sitting?"
        message={
          removing === null
            ? ''
            : `${removing.subjectName} for ${removing.sectionName} on ${formatDate(removing.examDate)} at ${orDash(toInputTime(removing.startTime))} will be removed from the timetable.`
        }
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing !== null && remove.mutate(removing)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
